import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getPolicyPremiumTotal } from "../utils/policyCalc";
import { Calendar as BigCalendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import DataStateError from "../components/DataStateError";
import { Badge } from "../components/ui/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../components/ui/hover-card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
// 29/09/2026 — lista canonica dei tipi per il filtro di esportazione.
import { POLICY_TYPES } from "../components/policies";
import { 
  ChevronLeft, 
  ChevronRight, 
  FileText, 
  Calendar as CalendarIcon, 
  Download, 
  FileSpreadsheet,
  MoreVertical,
  Phone,
  MessageCircle,
  Mail,
  RefreshCw,
  CheckCircle,
  StickyNote,
  Eye,
  User,
  List,
  CalendarDays,
  LayoutGrid,
  Focus,
  Filter,
  X,
  Euro,
  Building2,
  ExternalLink
} from "lucide-react";
import { 
  format, 
  parse, 
  startOfWeek, 
  getDay, 
  addMonths, 
  subMonths,
  startOfDay,
  endOfDay,
  isSameDay,
  addDays,
  subDays
} from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";
import { EXPORT_COLUMNS } from "../components/ExportColumnsDialog";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { formatCurrency, formatDate, formatMonthYear } from "../lib/format";

// Configure date-fns localizer for react-big-calendar
const locales = { 'it': it };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

// Enable drag and drop
const DnDCalendar = withDragAndDrop(BigCalendar);

function CalendarioScadenze({ trascinabile = true, ...props }) {
  const Componente = trascinabile ? DnDCalendar : BigCalendar;
  return <Componente {...props} />;
}

// Custom messages in Italian
const messages = {
  today: 'Oggi',
  previous: 'Precedente',
  next: 'Successivo',
  month: 'Mese',
  week: 'Settimana',
  day: 'Giorno',
  agenda: 'Agenda',
  date: 'Data',
  time: 'Ora',
  event: 'Evento',
  allDay: 'Tutto il giorno',
  noEventsInRange: 'Nessun evento in questo periodo',
  showMore: (total) => `+${total} altri`,
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // 'month', 'week', 'day', 'agenda'
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // Se il caricamento fallisce, il calendario vuoto farebbe credere che non ci
  // siano scadenze: e' la pagina dove questo errore costa di piu'.
  const [loadError, setLoadError] = useState(false);
  // Sul telefono il trascinamento di una scadenza parte facilmente mentre si
  // scorre e sposta DAVVERO le date della polizza: meglio disattivarlo.
  const [dispositivoTouch, setDispositivoTouch] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    expiry: true,
    semiannual: true,
    start: true
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Focus mode (today with highlight)
  const [focusMode, setFocusMode] = useState(false);
  
  // Quick note dialog
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [quickNote, setQuickNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  
  // Event details dialog
  const [eventDialogOpen, setEventDialogOpen] = useState(false);

  // Export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    format: "pdf",
    orientation: "landscape",
    filter: "all"
  });
  const calendarColumns = EXPORT_COLUMNS.expiring || [];
  const [selectedExportCols, setSelectedExportCols] = useState(
    calendarColumns.filter(c => c.default).map(c => c.key)
  );
  const [exportSortBy, setExportSortBy] = useState("");
  const [exportSortDir, setExportSortDir] = useState("asc");

  // Fetch events for current view range
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch 3 months worth of data for smooth navigation
      const promises = [-1, 0, 1].map(offset => {
        const date = addMonths(currentDate, offset);
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        return axios.get(`/api/calendar/events?month=${month}&year=${year}`);
      });
      
      const responses = await Promise.all(promises);
      const allEvents = responses.flatMap(r => r.data);
      
      // Remove duplicates and transform to calendar format
      const uniqueEvents = Array.from(
        new Map(allEvents.map(e => [`${e.policy_id}-${e.type}-${e.date}`, e])).values()
      );
      
      // Transform events for react-big-calendar
      const calendarEvents = uniqueEvents.map(e => {
        // Parse date as LOCAL (not UTC) to avoid timezone shift
        const [y, m, d] = e.date.split("-").map(Number);
        const eventDate = new Date(y, m - 1, d);
        return {
          ...e,
          id: `${e.policy_id}-${e.type}-${e.date}`,
          title: `${e.client_name} - ${e.title}`,
          start: eventDate,
          end: eventDate,
          allDay: true,
          resource: e
        };
      });
      
      setEvents(calendarEvents);
      setLoadError(false);
    } catch (error) {
      setLoadError(true);
      toast.error("Errore nel caricamento degli eventi");
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    const tocco =
      (typeof window !== 'undefined' && 'ontouchstart' in window) ||
      (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) ||
      (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: none)').matches);
    setDispositivoTouch(!!tocco);
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Filter events based on active filters
  const filteredEvents = useMemo(() => {
    return events.filter(e => {
      if (e.resource.type === 'expiry' && !filters.expiry) return false;
      if (e.resource.type === 'semiannual' && !filters.semiannual) return false;
      if (e.resource.type === 'start' && !filters.start) return false;
      return true;
    });
  }, [events, filters]);

  // Focus mode: filter to show only today's events prominently
  const displayEvents = useMemo(() => {
    if (!focusMode) return filteredEvents;
    const today = startOfDay(new Date());
    const endToday = endOfDay(new Date());
    return filteredEvents.filter(e => {
      const eventDate = new Date(e.start);
      return eventDate >= today && eventDate <= endToday;
    });
  }, [filteredEvents, focusMode]);

  // Event styling based on payment status
  // Verde = pagata/rinnovata, Rosso = da rinnovare/scaduta, Arancione = semestre non pagato
  const eventStyleGetter = useCallback((event) => {
    const res = event.resource || {};
    const type = res.type;
    let style = {
      borderRadius: '4px',
      border: 'none',
      fontSize: '0.75rem',
      padding: '2px 6px',
    };
    
    if (type === 'start' || res.renewed || res.paid) {
      // Verde: inizio polizza oppure scadenza già rinnovata/pagata
      style.backgroundColor = '#16a34a'; // green-600
    } else if (type === 'semiannual') {
      // Arancione se non pagata, Verde se pagata
      style.backgroundColor = res.semiannual_paid ? '#16a34a' : '#ea580c';
    } else if (type === 'expiry') {
      // Rosso: scadenza da rinnovare
      style.backgroundColor = '#dc2626'; // red-600
    } else {
      style.backgroundColor = '#6b7280';
    }
    style.color = 'white';
    
    return { style };
  }, []);

  // Handle drag and drop - update event date
  const handleEventDrop = useCallback(async ({ event, start }) => {
    const newDate = format(start, 'yyyy-MM-dd');
    const originalEvent = event.resource;
    
    try {
      // Determine which date field to update based on event type
      let updateField = '';
      switch (originalEvent.type) {
        case 'expiry':
          updateField = 'expiry_date';
          break;
        case 'semiannual':
          updateField = 'semiannual_expiry';
          break;
        case 'start':
          updateField = 'start_date';
          break;
        default:
          return;
      }
      
      // Call API to update the policy date
      await axios.patch(`/api/policies/${originalEvent.policy_id}`, {
        [updateField]: newDate
      });
      
      // Update local state
      setEvents(prev => prev.map(e => {
        if (e.id === event.id) {
          return {
            ...e,
            start: new Date(newDate),
            end: new Date(newDate),
            resource: { ...e.resource, date: newDate }
          };
        }
        return e;
      }));
      
      toast.success(`Data aggiornata a ${formatDate(start)}`);
    } catch (error) {
      toast.error("Errore nell'aggiornamento della data");
      fetchEvents(); // Refresh to restore original state
    }
  }, [fetchEvents]);

  // Quick Actions
  const handleCall = (event) => {
    const resource = event.resource || event;
    if (resource.phone) {
      window.location.href = `tel:${resource.phone}`;
    } else {
      toast.error("Nessun numero di telefono disponibile");
    }
  };

  const handleWhatsApp = (event) => {
    const resource = event.resource || event;
    if (resource.phone) {
      const cleanPhone = resource.phone.replace(/\D/g, '');
      const phone = cleanPhone.startsWith('39') ? cleanPhone : `39${cleanPhone}`;
      const message = encodeURIComponent(
        `Buongiorno ${resource.client_name}, la contatto in merito alla sua polizza ${resource.company || ''} in scadenza il ${resource.date}. Cordiali saluti.`
      );
      window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    } else {
      toast.error("Nessun numero di telefono disponibile");
    }
  };

  const handleEmail = (event) => {
    const resource = event.resource || event;
    if (resource.email) {
      const subject = encodeURIComponent(`Promemoria scadenza polizza ${resource.company || ''}`);
      const body = encodeURIComponent(
        `Gentile ${resource.client_name},\n\nLe ricordo che la Sua polizza ${resource.company || ''} ${resource.policy_number ? `n. ${resource.policy_number}` : ''} è in scadenza il ${resource.date}.\n\nResto a disposizione per il rinnovo.\n\nCordiali saluti`
      );
      window.location.href = `mailto:${resource.email}?subject=${subject}&body=${body}`;
    } else {
      toast.error("Nessun indirizzo email disponibile");
    }
  };

  const handleMarkCompleted = async (event) => {
    const resource = event.resource || event;
    try {
      await axios.post(`/api/calendar/events/${resource.policy_id}/mark-completed`, {
        event_type: resource.type,
        event_date: resource.date
      });
      toast.success("Evento segnato come completato");
      fetchEvents();
    } catch (error) {
      toast.error("Errore nel segnare l'evento come completato");
    }
  };

  const openNoteDialog = (event) => {
    setSelectedEvent(event);
    setQuickNote("");
    setNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    if (!selectedEvent || !quickNote.trim()) return;
    
    setSavingNote(true);
    const resource = selectedEvent.resource || selectedEvent;
    try {
      await axios.post(`/api/policies/${resource.policy_id}/add-note`, {
        note: quickNote,
        context: `[Calendario ${resource.date}]`
      });
      toast.success("Nota aggiunta alla polizza");
      setNoteDialogOpen(false);
    } catch (error) {
      toast.error("Errore nel salvare la nota");
    } finally {
      setSavingNote(false);
    }
  };

  const handleGoToPolicy = (event) => {
    const resource = event.resource || event;
    // Navigate directly to policy detail page
    navigate(`/policies/${resource.policy_id}`);
  };

  const handleGoToClient = (event) => {
    const resource = event.resource || event;
    if (resource.client_id) {
      navigate(`/clients/${resource.client_id}`);
    }
  };

  // Event click - navigate directly to policy detail
  const handleSelectEvent = (event) => {
    const resource = event.resource || event;
    // Navigate directly to the policy detail page
    navigate(`/policies/${resource.policy_id}`);
  };

  // Calendar navigation
  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  const handleViewChange = (newView) => {
    setView(newView);
    if (focusMode && newView !== 'day') {
      setFocusMode(false);
    }
  };

  // Go to today with focus mode
  const handleTodayFocus = () => {
    setCurrentDate(new Date());
    setView('day');
    setFocusMode(true);
  };

  // Export functions
  const handleExport = async () => {
    setExporting(true);
    setExportDialogOpen(false);
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const { format: fmt, orientation, filter } = exportOptions;
      
      const params = new URLSearchParams({
        format: fmt,
        month,
        year,
        orientation,
        filter_type: filter
      });
      
      if (fmt === "pdf" && selectedExportCols.length > 0) {
        params.append("columns", selectedExportCols.join(","));
      }
      if (exportSortBy && exportSortBy !== "none") {
        params.append("sort_by", exportSortBy);
        params.append("sort_dir", exportSortDir);
      }
      
      const response = await axios.get(
        `/api/export/expiring?${params.toString()}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `scadenze_${month}_${year}.${fmt === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Report ${fmt.toUpperCase()} scaricato`);
    } catch (error) {
      toast.error("Errore nell'esportazione");
    } finally {
      setExporting(false);
    }
  };

  // Get event type info
  const getEventTypeInfo = (type) => {
    switch (type) {
      case 'expiry':
        return { label: 'Scadenza Annuale', color: 'bg-red-500', icon: '🔴' };
      case 'semiannual':
        return { label: 'Rata Semestrale', color: 'bg-orange-500', icon: '🟠' };
      case 'start':
        return { label: 'Inizio Polizza', color: 'bg-green-500', icon: '🟢' };
      default:
        return { label: 'Evento', color: 'bg-gray-500', icon: '⚪' };
    }
  };

  // Stats for current month
  const monthStats = useMemo(() => {
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const monthEvents = filteredEvents.filter(e => {
      const eventDate = new Date(e.start);
      return eventDate.getMonth() === month && eventDate.getFullYear() === year;
    });
    
    return {
      expiry: monthEvents.filter(e => e.resource.type === 'expiry').length,
      semiannual: monthEvents.filter(e => e.resource.type === 'semiannual').length,
      start: monthEvents.filter(e => e.resource.type === 'start').length,
      total: monthEvents.length
    };
  }, [filteredEvents, currentDate]);

  // Today's events for focus panel
  const todayEvents = useMemo(() => {
    const today = startOfDay(new Date());
    return filteredEvents.filter(e => isSameDay(new Date(e.start), today));
  }, [filteredEvents]);

  // Custom toolbar component
  const CustomToolbar = ({ label, onNavigate, onView, view: currentView }) => (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 p-4 bg-muted/30 rounded-lg">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => onNavigate('PREV')} aria-label="Periodo precedente">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" onClick={() => onNavigate('TODAY')}>
          Oggi
        </Button>
        <Button variant="outline" size="icon" onClick={() => onNavigate('NEXT')} aria-label="Periodo successivo">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <h2 className="text-xl font-semibold font-heading capitalize ml-2">
          {label}
        </h2>
      </div>
      
      <div className="flex items-center gap-2 flex-wrap">
        {/* View buttons */}
        <div className="flex border rounded-lg overflow-hidden">
          <Button
            variant={currentView === 'month' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onView('month')}
            className="rounded-none"
          >
            <LayoutGrid className="h-4 w-4 mr-1" />
            Mese
          </Button>
          <Button
            variant={currentView === 'week' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onView('week')}
            className="rounded-none border-x"
          >
            <CalendarDays className="h-4 w-4 mr-1" />
            Settimana
          </Button>
          <Button
            variant={currentView === 'agenda' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onView('agenda')}
            className="rounded-none"
          >
            <List className="h-4 w-4 mr-1" />
            Agenda
          </Button>
        </div>
        
        {/* Focus Today button */}
        <Button
          variant={focusMode ? 'default' : 'outline'}
          size="sm"
          onClick={handleTodayFocus}
          className={focusMode ? 'bg-blue-600 hover:bg-blue-700' : ''}
        >
          <Focus className="h-4 w-4 mr-1" />
          Focus Oggi
        </Button>
        
        {/* Filter button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className={showFilters ? 'bg-muted' : ''}
        >
          <Filter className="h-4 w-4 mr-1" />
          Filtri
        </Button>
      </div>
    </div>
  );

  // Custom agenda event component
  const AgendaEvent = ({ event }) => {
    const typeInfo = getEventTypeInfo(event.resource?.type);
    return (
      <div className="flex items-center gap-3 py-2 px-3 hover:bg-muted/50 rounded cursor-pointer">
        <span className="text-lg">{typeInfo.icon}</span>
        <div className="flex-1">
          <p className="font-medium">{event.resource?.client_name}</p>
          <p className="text-sm text-muted-foreground">{event.resource?.title}</p>
          {event.resource?.company && (
            <p className="text-xs text-muted-foreground">{event.resource.company}</p>
          )}
        </div>
        {event.resource?.premium > 0 && (
          <Badge variant="outline" className="ml-auto">
            €{getPolicyPremiumTotal(event.resource).toFixed(0)}
          </Badge>
        )}
      </div>
    );
  };

  // Custom event component with HoverCard for month/week views
  const EventWithTooltip = ({ event }) => {
    const typeInfo = getEventTypeInfo(event.resource?.type);
    const resource = event.resource || {};
    
    return (
      <HoverCard openDelay={200} closeDelay={100}>
        <HoverCardTrigger asChild>
          <div className="truncate text-xs px-1 cursor-pointer h-full flex items-center">
            {event.title}
          </div>
        </HoverCardTrigger>
        <HoverCardContent 
          side="right" 
          align="start" 
          className="w-72 p-0 overflow-hidden"
          sideOffset={5}
        >
          {/* Header with color based on event type */}
          <div 
            className="p-3 text-white"
            style={{ 
              backgroundColor: typeInfo.icon === '🔴' ? '#dc2626' : 
                              typeInfo.icon === '🟠' ? '#ea580c' : 
                              typeInfo.icon === '🟢' ? '#16a34a' : '#6b7280'
            }}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">{typeInfo.icon}</span>
              <div>
                <p className="font-semibold">{typeInfo.label}</p>
                <p className="text-xs opacity-90">{formatDate(event.start)}</p>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-3 space-y-3">
            {/* Client */}
            <div className="flex items-start gap-2">
              <User className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium text-sm">{resource.client_name}</p>
                {resource.phone && (
                  <p className="text-xs text-muted-foreground">{resource.phone}</p>
                )}
              </div>
            </div>
            
            {/* Company */}
            {resource.company && (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm">{resource.company}</p>
              </div>
            )}
            
            {/* Policy Number */}
            {resource.policy_number && (
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-mono">{resource.policy_number}</p>
              </div>
            )}
            
            {/* Premium */}
            {resource.premium > 0 && (
              <div className="flex items-center gap-2">
                <Euro className="h-4 w-4 text-green-600" />
                <p className="text-sm font-semibold text-green-600">
                  €{getPolicyPremiumTotal(resource).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                </p>
              </div>
            )}
            
            {/* Quick action hint */}
            <div className="pt-2 border-t flex items-center justify-between text-xs text-muted-foreground">
              <span>Click per dettagli</span>
              <ExternalLink className="h-3 w-3" />
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  };

  if (loading && events.length === 0) {
    return (
      <div data-testid="calendar-page-loading" className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <Skeleton className="h-10 w-64 mb-2" />
            <Skeleton className="h-4 w-80" />
          </div>
          <Skeleton className="h-10 w-40" />
        </div>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <Skeleton className="h-[500px] w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loadError && events.length === 0) {
    return (
      <div data-testid="calendar-page-errore" className="space-y-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground tracking-tight">
            Calendario Scadenze
          </h1>
        </div>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <DataStateError
              titolo="Non riesco a leggere le scadenze"
              messaggio="I dati non sono andati persi: e' il collegamento che non ha risposto. Un calendario vuoto qui NON significa che non ci siano scadenze."
              onRetry={fetchEvents}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="calendar-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground tracking-tight">
            Calendario Scadenze
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Visualizza e gestisci tutte le scadenze delle polizze
          </p>
        </div>
        
        {/* Export Button */}
        <Button 
          variant="outline" 
          disabled={exporting}
          onClick={() => setExportDialogOpen(true)}
          data-testid="export-btn"
        >
          <Download className="mr-2 h-4 w-4" />
          {exporting ? "Esportazione..." : "Esporta Scadenze"}
        </Button>
      </div>

      {/* Quick Filters Panel */}
      {showFilters && (
        <Card className="bg-card border-border" data-testid="filters-panel">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-6">
              <span className="text-sm font-medium">Mostra:</span>
              
              <div className="flex items-center gap-2">
                <Switch
                  id="filter-expiry"
                  checked={filters.expiry}
                  onCheckedChange={(checked) => setFilters(f => ({ ...f, expiry: checked }))}
                />
                <Label htmlFor="filter-expiry" className="flex items-center gap-2 cursor-pointer">
                  <div className="w-3 h-3 bg-red-500 rounded-sm" />
                  Scadenze Annuali
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  id="filter-semiannual"
                  checked={filters.semiannual}
                  onCheckedChange={(checked) => setFilters(f => ({ ...f, semiannual: checked }))}
                />
                <Label htmlFor="filter-semiannual" className="flex items-center gap-2 cursor-pointer">
                  <div className="w-3 h-3 bg-orange-500 rounded-sm" />
                  Rate Semestrali
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  id="filter-start"
                  checked={filters.start}
                  onCheckedChange={(checked) => setFilters(f => ({ ...f, start: checked }))}
                />
                <Label htmlFor="filter-start" className="flex items-center gap-2 cursor-pointer">
                  <div className="w-3 h-3 bg-green-500 rounded-sm" />
                  Inizio Polizze
                </Label>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters({ expiry: true, semiannual: true, start: true })}
                className="ml-auto"
              >
                Mostra Tutti
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Focus Panel - shown when in focus mode or has today events */}
      {(focusMode || todayEvents.length > 0) && view !== 'agenda' && (
        <Card className={`border-2 ${focusMode ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20' : 'border-border'}`} data-testid="today-focus-panel">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-heading">
                <Focus className="h-5 w-5 text-blue-500" />
                Oggi - {formatDate(new Date())}
              </div>
              {focusMode && (
                <Button variant="ghost" size="sm" onClick={() => setFocusMode(false)}>
                  <X className="h-4 w-4 mr-1" />
                  Chiudi Focus
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todayEvents.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                Nessuna scadenza oggi 🎉
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {todayEvents.map((event, idx) => {
                  const typeInfo = getEventTypeInfo(event.resource?.type);
                  return (
                    <div
                      key={idx}
                      className="p-3 rounded-lg border bg-card hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => handleSelectEvent(event)}
                    >
                      <div className="flex items-start gap-2">
                        <span>{typeInfo.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{event.resource?.client_name}</p>
                          <p className="text-sm text-muted-foreground truncate">{event.resource?.company}</p>
                          {event.resource?.premium > 0 && (
                            <p className="text-sm font-semibold mt-1">{formatCurrency(getPolicyPremiumTotal(event.resource))}</p>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Altre azioni">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleCall(event)}>
                              <Phone className="h-4 w-4 mr-2" />
                              Chiama
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleWhatsApp(event)}>
                              <MessageCircle className="h-4 w-4 mr-2" />
                              WhatsApp
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEmail(event)}>
                              <Mail className="h-4 w-4 mr-2" />
                              Email
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleMarkCompleted(event)}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Completato
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Calendar */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <style>{`
            .rbc-calendar {
              font-family: 'Inter', sans-serif;
            }
            .rbc-toolbar {
              display: none;
            }
            .rbc-header {
              padding: 8px;
              font-weight: 600;
              background: hsl(var(--muted));
              border-bottom: 1px solid hsl(var(--border));
            }
            .rbc-month-view, .rbc-time-view, .rbc-agenda-view {
              border: 1px solid hsl(var(--border));
              border-radius: 8px;
              overflow: hidden;
            }
            .rbc-day-bg {
              background: hsl(var(--card));
            }
            .rbc-off-range-bg {
              background: hsl(var(--muted) / 0.5);
            }
            .rbc-today {
              background: hsl(var(--primary) / 0.1) !important;
            }
            .rbc-event {
              border-radius: 4px !important;
            }
            .rbc-event:focus {
              outline: 2px solid hsl(var(--ring));
              outline-offset: 2px;
            }
            .rbc-show-more {
              color: hsl(var(--primary));
              font-weight: 500;
            }
            .rbc-agenda-table {
              border: none;
            }
            .rbc-agenda-date-cell, .rbc-agenda-time-cell {
              padding: 12px;
              border-bottom: 1px solid hsl(var(--border));
            }
            .rbc-agenda-event-cell {
              padding: 8px;
              border-bottom: 1px solid hsl(var(--border));
            }
            .rbc-time-header-cell {
              min-height: 60px;
            }
            .rbc-time-content {
              border-top: 1px solid hsl(var(--border));
            }
            .rbc-timeslot-group {
              border-bottom: 1px solid hsl(var(--border) / 0.5);
            }
            .rbc-day-slot .rbc-time-slot {
              border-top: 1px solid hsl(var(--border) / 0.3);
            }
            .rbc-current-time-indicator {
              background-color: hsl(var(--destructive));
            }
            .rbc-addons-dnd .rbc-addons-dnd-resize-ns-icon,
            .rbc-addons-dnd .rbc-addons-dnd-resize-ew-icon {
              display: none;
            }
            .rbc-addons-dnd-dragged-event {
              opacity: 0.7;
            }
          `}</style>
          
          <CustomToolbar
            label={
              view === 'month'
                ? formatMonthYear(currentDate)
                : view === 'week'
                  ? `Settimana del ${formatDate(currentDate)}`
                  : formatDate(currentDate)
            }
            onNavigate={(action) => {
              if (action === 'TODAY') {
                setCurrentDate(new Date());
              } else if (action === 'PREV') {
                setCurrentDate(view === 'month' ? subMonths(currentDate, 1) : subDays(currentDate, view === 'week' ? 7 : 1));
              } else if (action === 'NEXT') {
                setCurrentDate(view === 'month' ? addMonths(currentDate, 1) : addDays(currentDate, view === 'week' ? 7 : 1));
              }
            }}
            onView={handleViewChange}
            view={view}
          />
          
          <CalendarioScadenze
              trascinabile={!dispositivoTouch}
            localizer={localizer}
            events={displayEvents}
            startAccessor="start"
            endAccessor="end"
            date={currentDate}
            onNavigate={handleNavigate}
            view={view}
            onView={handleViewChange}
            onSelectEvent={handleSelectEvent}
            {...(dispositivoTouch ? {} : { onEventDrop: handleEventDrop })}
            eventPropGetter={eventStyleGetter}
            messages={messages}
            culture="it"
            style={{ height: view === 'agenda' ? 'auto' : 600, minHeight: 400 }}
            views={['month', 'week', 'day', 'agenda']}
            step={60}
            timeslots={1}
            min={new Date(2024, 0, 1, 8, 0)}
            max={new Date(2024, 0, 1, 20, 0)}
            draggableAccessor={() => !dispositivoTouch}
            resizable={false}
            popup
            components={{
              event: EventWithTooltip,
              agenda: {
                event: AgendaEvent
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Monthly Summary */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading">
            Riepilogo {formatMonthYear(currentDate)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-900">
              <p className="text-sm text-muted-foreground">Scadenze Annuali</p>
              <p className="text-2xl font-bold text-red-600">{monthStats.expiry}</p>
            </div>
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-900">
              <p className="text-sm text-muted-foreground">Rate Semestrali</p>
              <p className="text-2xl font-bold text-orange-600">{monthStats.semiannual}</p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-900">
              <p className="text-sm text-muted-foreground">Nuove Polizze</p>
              <p className="text-2xl font-bold text-green-600">{monthStats.start}</p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg border border-border">
              <p className="text-sm text-muted-foreground">Totale Eventi</p>
              <p className="text-2xl font-bold text-foreground">{monthStats.total}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Event Details Dialog */}
      <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Dettagli Evento
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${getEventTypeInfo(selectedEvent.resource?.type).color}/10`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">{getEventTypeInfo(selectedEvent.resource?.type).icon}</span>
                  <Badge>{getEventTypeInfo(selectedEvent.resource?.type).label}</Badge>
                </div>
                <p className="font-semibold text-lg">{selectedEvent.resource?.client_name}</p>
                <p className="text-muted-foreground">{selectedEvent.resource?.title}</p>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Data</span>
                  <span className="font-medium">{formatDate(selectedEvent.start)}</span>
                </div>
                {selectedEvent.resource?.company && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Compagnia</span>
                    <span className="font-medium">{selectedEvent.resource.company}</span>
                  </div>
                )}
                {selectedEvent.resource?.policy_number && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">N. Polizza</span>
                    <span className="font-medium">{selectedEvent.resource.policy_number}</span>
                  </div>
                )}
                {selectedEvent.resource?.premium > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Premio</span>
                    <span className="font-medium">{formatCurrency(getPolicyPremiumTotal(selectedEvent.resource))}</span>
                  </div>
                )}
                {selectedEvent.resource?.phone && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Telefono</span>
                    <span className="font-medium">{selectedEvent.resource.phone}</span>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-3 gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => handleCall(selectedEvent)}>
                  <Phone className="h-4 w-4 mr-1" />
                  Chiama
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleWhatsApp(selectedEvent)}>
                  <MessageCircle className="h-4 w-4 mr-1" />
                  WhatsApp
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleEmail(selectedEvent)}>
                  <Mail className="h-4 w-4 mr-1" />
                  Email
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => { setEventDialogOpen(false); handleGoToPolicy(selectedEvent); }}>
                  <Eye className="h-4 w-4 mr-1" />
                  Vai alla Polizza
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setEventDialogOpen(false); handleGoToClient(selectedEvent); }}>
                  <User className="h-4 w-4 mr-1" />
                  Vai al Cliente
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" onClick={() => { setEventDialogOpen(false); openNoteDialog(selectedEvent); }}>
                  <StickyNote className="h-4 w-4 mr-1" />
                  Aggiungi Nota
                </Button>
                <Button variant="default" size="sm" onClick={() => { handleMarkCompleted(selectedEvent); setEventDialogOpen(false); }}>
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Completato
                </Button>
              </div>
              
              {(selectedEvent.resource?.type === 'expiry' || selectedEvent.resource?.type === 'semiannual') && (
                <Button 
                  className="w-full" 
                  onClick={() => {
                    setEventDialogOpen(false);
                    navigate(`/policies?action=renew&id=${selectedEvent.resource.policy_id}`);
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Gestisci Rinnovo
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5" />
              Aggiungi Nota Rapida
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedEvent.resource?.client_name}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedEvent.resource?.company} • {selectedEvent.resource?.title}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="quickNote">Nota</Label>
                <Textarea
                  id="quickNote"
                  value={quickNote}
                  onChange={(e) => setQuickNote(e.target.value)}
                  placeholder="Scrivi una nota..."
                  rows={4}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveNote} disabled={savingNote || !quickNote.trim()}>
              {savingNote ? "Salvataggio..." : "Salva Nota"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Options Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Esporta Scadenze
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Format */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Formato</Label>
              <div className="flex gap-2">
                <Button
                  variant={exportOptions.format === "pdf" ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setExportOptions(prev => ({ ...prev, format: "pdf" }))}
                  data-testid="export-format-pdf"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  PDF
                </Button>
                <Button
                  variant={exportOptions.format === "excel" ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setExportOptions(prev => ({ ...prev, format: "excel" }))}
                  data-testid="export-format-excel"
                >
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Excel
                </Button>
              </div>
            </div>

            {/* Orientation (only for PDF) */}
            {exportOptions.format === "pdf" && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Orientamento</Label>
                <div className="flex gap-2">
                  <Button
                    variant={exportOptions.orientation === "landscape" ? "default" : "outline"}
                    size="sm"
                    className="flex-1"
                    onClick={() => setExportOptions(prev => ({ ...prev, orientation: "landscape" }))}
                    data-testid="export-orientation-landscape"
                  >
                    <LayoutGrid className="mr-2 h-4 w-4" />
                    Orizzontale
                  </Button>
                  <Button
                    variant={exportOptions.orientation === "portrait" ? "default" : "outline"}
                    size="sm"
                    className="flex-1"
                    onClick={() => setExportOptions(prev => ({ ...prev, orientation: "portrait" }))}
                    data-testid="export-orientation-portrait"
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Verticale
                  </Button>
                </div>
              </div>
            )}

            {/* Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Filtra per</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={exportOptions.filter}
                onChange={(e) => setExportOptions(prev => ({ ...prev, filter: e.target.value }))}
                data-testid="export-filter-select"
              >
                <option value="all">Tutte le scadenze</option>
                <option value="da_rinnovare">Solo da rinnovare</option>
                <option value="rinnovate">Solo rinnovate</option>
                <option value="non_rinnovate">Solo non rinnovate</option>
                <optgroup label="Per tipo polizza">
                  {/* 29/09/2026 — opzioni dalla lista CANONICA (components/policies):
                      cosi' non manca piu' nessun tipo (prima mancavano autocarri,
                      altri_veicoli, targa_prova, rct, vettoriale, condominio,
                      rc_caccia e il nuovo cvt). */}
                  {POLICY_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </optgroup>
              </select>
            </div>

            {/* Column selection for PDF */}
            {exportOptions.format === "pdf" && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Colonne ({selectedExportCols.length}/{calendarColumns.length})</Label>
                <div className="grid grid-cols-2 gap-2 p-3 rounded-md border bg-muted/30 max-h-40 overflow-y-auto">
                  {calendarColumns.map(col => (
                    <div key={col.key} className="flex items-center gap-2">
                      <Checkbox
                        id={`cal-col-${col.key}`}
                        checked={selectedExportCols.includes(col.key)}
                        onCheckedChange={(checked) => {
                          setSelectedExportCols(prev =>
                            checked ? [...prev, col.key] : prev.filter(k => k !== col.key)
                          );
                        }}
                        data-testid={`export-col-${col.key}`}
                      />
                      <Label htmlFor={`cal-col-${col.key}`} className="text-sm cursor-pointer">
                        {col.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sort options */}
            {exportOptions.format === "pdf" && (
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-1.5">
                  <ArrowUpDown className="h-4 w-4" />
                  Ordina per
                </Label>
                <div className="flex gap-2">
                  <select
                    value={exportSortBy}
                    onChange={e => setExportSortBy(e.target.value)}
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="export-sort-column"
                  >
                    <option value="">Nessun ordinamento</option>
                    {calendarColumns.filter(c => selectedExportCols.includes(c.key)).map(col => (
                      <option key={col.key} value={col.key}>{col.label}</option>
                    ))}
                  </select>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setExportSortDir(d => d === "asc" ? "desc" : "asc")}
                    disabled={!exportSortBy}
                    className="shrink-0"
                    data-testid="export-sort-direction"
                   aria-label="Cambia ordinamento">
                    {exportSortDir === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleExport} disabled={exporting} data-testid="export-confirm-btn">
              <Download className="mr-2 h-4 w-4" />
              {exporting ? "Esportazione..." : "Esporta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
